import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { rsvpApi } from "../../api/rsvp";
import { PublicTierSignInForm } from "../../components/events/PublicTierSignInForm";
import { PublicTicketTemplateShell } from "../../components/ticketTemplate/PublicTicketTemplateShell";
import "../../components/ticketTemplate/ticketTemplate.css";
import "../../components/ticketTemplate/zoniqTicketTemplate.css";
import { queryKeys } from "../../queryKeys";
import { DEFAULT_TICKET_TEMPLATE_CONFIG } from "../../types/rsvp";
import { findSettingsByTokenLocal } from "../../utils/rsvpStore";
import { mergeTemplateConfig } from "../../utils/ticketTemplateStore";

const PublicGuestListPage: React.FC = () => {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const [done, setDone] = useState(false);
  const [guestName, setGuestName] = useState("");

  const metaQuery = useQuery({
    queryKey: inviteToken ? queryKeys.publicGuestList(inviteToken) : ["guest-list", "idle"],
    queryFn: async () => {
      const meta = await rsvpApi.publicMeta(inviteToken!);
      if (meta.link_kind === "rsvp") {
        throw new Error("This Guest List link is invalid or has expired.");
      }
      if (!meta.link_kind) {
        const settings = findSettingsByTokenLocal(inviteToken!);
        if (settings && settings.guest_list_token !== inviteToken) {
          throw new Error("This Guest List link is invalid or has expired.");
        }
      }
      return meta;
    },
    enabled: Boolean(inviteToken),
    retry: 1,
  });

  const submitMutation = useMutation({
    mutationFn: async (values: {
      name: string;
      phone: string;
      email?: string;
      tier_counts: Record<string, number>;
      party_size: number;
      promoter?: string;
      promoter_id?: string;
      whatsapp_opt_in: boolean;
    }) => {
      if (!inviteToken) throw new Error("Missing invite");
      return rsvpApi.publicSubmit(inviteToken, {
        name: values.name,
        phone: values.phone,
        email: values.email ?? null,
        party_size: values.party_size,
        tier_counts: values.tier_counts,
        category: "stag",
        promoter: values.promoter,
        promoter_id: values.promoter_id,
        source: "guest_list",
        channel: "guest_list",
        whatsapp_opt_in: values.whatsapp_opt_in,
      });
    },
    onSuccess: (_data, values) => {
      setGuestName(values.name);
      setDone(true);
      toast.success("You're on the guest list");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not submit"),
  });

  if (!inviteToken) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body">
            <p className="zticket-hint">Invalid Guest List link.</p>
          </div>
        </div>
      </div>
    );
  }

  if (metaQuery.isPending) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body">
            <p className="zticket-hint">Loading event…</p>
          </div>
        </div>
      </div>
    );
  }

  if (metaQuery.isError || !metaQuery.data) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body zticket-status">
            <h2 className="zticket-status__title">Link unavailable</h2>
            <p className="zticket-status__text">
              {metaQuery.error instanceof Error
                ? metaQuery.error.message
                : "This Guest List link is invalid or has expired."}
            </p>
            <Link to="/" className="eventos-nav-btn">
              Go to Zoniq
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const meta = metaQuery.data;
  const templateId = meta.ticket_template_id ?? "black";
  const templateConfig = mergeTemplateConfig(meta.template_config ?? DEFAULT_TICKET_TEMPLATE_CONFIG);

  if (done) {
    return (
      <PublicTicketTemplateShell
        templateId={templateId}
        config={templateConfig}
        meta={{ ...meta, link_kind: "guest_list" }}
        eyebrow="Guest List"
      >
        <div className="zticket-status">
          <h2 className="zticket-status__title">You&apos;re on the guest list</h2>
          <p className="zticket-status__text">
            Thanks, {guestName || "guest"}. See you at {meta.event_name}.
          </p>
        </div>
      </PublicTicketTemplateShell>
    );
  }

  return (
    <PublicTicketTemplateShell
      templateId={templateId}
      config={templateConfig}
      meta={{ ...meta, link_kind: "guest_list" }}
      eyebrow="Guest List"
    >
      <PublicTierSignInForm
        // Guest List asks "how many of you" only — no tier, no cost.
        tiers={[]}
        partySizeOnly
        includePromoter
        promoters={meta.promoters ?? []}
        // Always bold + mandatory here, whatever the template config says: a
        // Guest List signup without a matching promoter code is discarded.
        promoterDisplay="bold"
        requirePromoterMatch
        submitLabel="Get my pass"
        pending={submitMutation.isPending}
        onSubmit={(values) => submitMutation.mutate(values)}
        submitError={submitMutation.error}
      />
    </PublicTicketTemplateShell>
  );
};

export default PublicGuestListPage;
